import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Heart, Cloud, Sparkles, Waves, RefreshCw, Shield, Brain, Zap, ArrowLeft } from "lucide-react";
import { GriefTools } from "./GriefTools";
import { AnxietyTools } from "./AnxietyTools";
import { DepressionTools } from "./DepressionTools";
import { TraumaTools } from "./TraumaTools";
import { OCDTools } from "./OCDTools";
import { StressTools } from "./StressTools";

interface ConditionHubProps {
  user: any;
}

export const ConditionHub = ({ user }: ConditionHubProps) => {
  const [selectedCondition, setSelectedCondition] = useState<string | null>(null);

  const conditions = [
    {
      id: 'grief',
      name: 'Grief & Loss',
      icon: Heart,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      borderColor: 'border-primary/20',
      description: 'Support for death of loved ones, relationship endings, health changes, or any significant loss',
      tools: ['Grief mapping', 'Memory processing', 'Meaning reconstruction', 'Continuing bonds']
    },
    {
      id: 'anxiety',
      name: 'Anxiety & Panic',
      icon: Zap,
      color: 'text-secondary',
      bgColor: 'bg-secondary/10',
      borderColor: 'border-secondary/20',
      description: 'Tools for general anxiety, panic attacks, social anxiety, and worry management',
      tools: ['Worry tree', 'Panic protocols', 'Exposure ladders', 'Relaxation training']
    },
    {
      id: 'depression',
      name: 'Depression',
      icon: Cloud,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      borderColor: 'border-primary/20',
      description: 'Evidence-based approaches for low mood, motivation, and behavioural activation',
      tools: ['Activity scheduling', 'Thought records', 'Behavioural activation', 'Pleasure mastery']
    },
    {
      id: 'trauma',
      name: 'Trauma & PTSD',
      icon: Shield,
      color: 'text-secondary',
      bgColor: 'bg-secondary/10',
      borderColor: 'border-secondary/20',
      description: 'Grounding techniques, flashback management, and trauma processing support',
      tools: ['Grounding techniques', 'Flashback protocols', 'Safe place imagery', 'Window of tolerance']
    },
    {
      id: 'ocd',
      name: 'OCD',
      icon: RefreshCw,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      borderColor: 'border-primary/20',
      description: 'Exposure Response Prevention (ERP) tools and compulsion management',
      tools: ['ERP tracker', 'Hierarchy builder', 'Ritual delays', 'Uncertainty tolerance']
    },
    {
      id: 'stress',
      name: 'Stress Management',
      icon: Waves,
      color: 'text-secondary',
      bgColor: 'bg-secondary/10',
      borderColor: 'border-secondary/20',
      description: 'Tools for work stress, life transitions, and burnout prevention',
      tools: ['Stress mapping', 'Boundary setting', 'Time management', 'Self-care planning']
    }
  ];

  if (selectedCondition === 'grief') {
    return (
      <div>
        <Button onClick={() => setSelectedCondition(null)} variant="outline" className="mb-6 text-base">
          <ArrowLeft className="h-5 w-5 mr-2" />
          Back to Conditions
        </Button>
        <GriefTools user={user} />
      </div>
    );
  }

  if (selectedCondition === 'anxiety') {
    return (
      <div>
        <Button onClick={() => setSelectedCondition(null)} variant="outline" className="mb-6">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Conditions
        </Button>
        <AnxietyTools user={user} />
      </div>
    );
  }

  if (selectedCondition === 'depression') {
    return (
      <div>
        <Button onClick={() => setSelectedCondition(null)} variant="outline" className="mb-6">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Conditions
        </Button>
        <DepressionTools user={user} />
      </div>
    );
  }

  if (selectedCondition === 'trauma') {
    return (
      <div>
        <Button onClick={() => setSelectedCondition(null)} variant="outline" className="mb-6">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Conditions
        </Button>
        <TraumaTools user={user} />
      </div>
    );
  }

  if (selectedCondition === 'ocd') {
    return (
      <div>
        <Button onClick={() => setSelectedCondition(null)} variant="outline" className="mb-6">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Conditions
        </Button>
        <OCDTools user={user} />
      </div>
    );
  }

  if (selectedCondition === 'stress') {
    return (
      <div>
        <Button onClick={() => setSelectedCondition(null)} variant="outline" className="mb-6">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Conditions
        </Button>
        <StressTools user={user} />
      </div>
    );
  }

  if (selectedCondition) {
    return (
      <div>
        <Button onClick={() => setSelectedCondition(null)} variant="outline" className="mb-6">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Conditions
        </Button>
        <Card className="p-8 text-center">
          <p className="text-muted-foreground">Tools for {selectedCondition} coming soon...</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-light text-foreground mb-3">Condition-Specific Support</h3>
        <p className="text-base text-muted-foreground max-w-2xl mx-auto leading-relaxed">
          Select a condition to access targeted, evidence-based tools and interventions designed for your specific needs
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {conditions.map((condition) => {
          const Icon = condition.icon;
          return (
            <Card 
              key={condition.id}
              className={`hover:shadow-therapy transition-all duration-300 cursor-pointer ${condition.borderColor} bg-gradient-to-br from-background via-background to-${condition.bgColor.split('/')[0]}/5`}
              onClick={() => setSelectedCondition(condition.id)}
            >
              <CardHeader>
                <div className="flex items-start justify-between mb-3">
                  <div className={`p-3 rounded-lg ${condition.bgColor}`}>
                    <Icon className={`h-7 w-7 ${condition.color}`} />
                  </div>
                  <Badge variant="secondary" className="text-sm">
                    {condition.tools.length} tools
                  </Badge>
                </div>
                <CardTitle className="text-xl mb-2">{condition.name}</CardTitle>
                <CardDescription className="text-base leading-relaxed">
                  {condition.description}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <p className="text-sm font-medium text-muted-foreground mb-2">Includes:</p>
                  <div className="flex flex-wrap gap-2">
                    {condition.tools.map((tool, idx) => (
                      <Badge key={idx} variant="outline" className="text-sm">
                        {tool}
                      </Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Educational Note */}
      <Card className="bg-gradient-calm border-border/50">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Brain className="h-6 w-6 text-primary mt-0.5" />
            <div className="space-y-1">
              <p className="text-base font-medium">Evidence-Based Approach</p>
              <p className="text-sm text-muted-foreground leading-relaxed">
                All tools are based on proven psychological interventions including CBT, DBT, ACT, and trauma-focused therapies.
                These are not replacements for professional therapy but can be powerful supplements to your reflection journey.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
